import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { DialogFormComponent } from '@modules/forms/components/dialog-form/dialog-form.component';
import { FormComponentInputs } from '@modules/forms/models/form-component-inputs';
import { FormOutputs } from '@modules/forms/models/form-outputs';
import { ModuleResolverInputs } from '@modules/forms/models/module-resolver-inputs';
import { Store } from '@ngrx/store';
import { SadmProtocol } from '@pixacare/dtos';
import { Form, FormAnswer } from '@pixacare/forms';
import { SadmEntityDraft } from '@shared/models/draft/sadm-entity-draft';
import { catchError, first, map, Observable, of, switchMap } from 'rxjs';
import { DialogService } from 'src/app/services/dialog.service';
import { selectSadmClient, selectSadmEntity } from 'src/app/shared/store/sadm/sadm.selectors';
import { SadmDialogFormInput } from '../components/sadm-dialog-form/sadm-dialog-form-input';
import { SadmDialogFormOutput } from '../components/sadm-dialog-form/sadm-dialog-form-output';
import { SadmDialogFormComponent } from '../components/sadm-dialog-form/sadm-dialog-form.component';
import { SadmEntityPickerOutput } from '../components/sadm-entity-picker/sadm-entity-picker-output';
import { SadmEntityPickerComponent } from '../components/sadm-entity-picker/sadm-entity-picker.component';
import { getLatestProtocol } from '../utils/get-latest-protocol';

@Injectable()
export class SadmAnalysisService {
  private readonly store = inject(Store);
  private readonly dialogService = inject(DialogService);
  private readonly translocoService = inject(TranslocoService);

  createAnalysis({
    clientCode,
    patientId,
    selectedSadmEntityId,
    formInputs,
  }: {
    clientCode: string;
    patientId: number;
    selectedSadmEntityId?: number;
    formInputs: ModuleResolverInputs;
  }): Observable<{
    sadmEntityId?: number;
    sadmEntityDraft?: SadmEntityDraft;
    analysisFormAnswer: FormAnswer;
    pictures?: File[];
  }> {
    return this.getOrCreateSadmEntity(clientCode, patientId, selectedSadmEntityId, formInputs).pipe(
      switchMap(({ sadmEntityId, sadmEntityDraft }) =>
        this.getForms(sadmEntityId, sadmEntityDraft?.sadmClientId).pipe(
          first(),
          switchMap(({ analysisForm }) => this.openDialogForm({ form: analysisForm, formInputs })),
          map(({ pictures, ...formAnswer }) => ({
            sadmEntityId,
            sadmEntityDraft,
            analysisFormAnswer: formAnswer,
            pictures: [...(pictures ?? [])],
          })),
        ),
      ),
      catchError((err) => {
        console.warn('Error while creating analysis', err);
        return of(null);
      }),
      first(),
    );
  }

  createAnalysisProtocol({
    clientCode,
    patientId,
    selectedSadmEntityId,
    formInputs,
  }: {
    clientCode: string;
    patientId: number;
    selectedSadmEntityId?: number;
    formInputs?: ModuleResolverInputs;
  }): Observable<{
    sadmEntityId?: number;
    sadmEntityDraft?: SadmEntityDraft;
    analysisFormAnswer: FormAnswer;
    protocolFormAnswer?: FormAnswer;
    pictures?: File[];
  }> {
    return this.getOrCreateSadmEntity(clientCode, patientId, selectedSadmEntityId, formInputs).pipe(
      switchMap(({ sadmEntityId, sadmEntityDraft }) =>
        this.getForms(sadmEntityId, sadmEntityDraft?.sadmClientId).pipe(
          first(),
          switchMap(({ analysisForm, protocolForm, protocolsHistory }) =>
            this.openSadmDialogForm({
              analysis: analysisForm && { form: analysisForm },
              protocol: protocolForm && {
                form: protocolForm,
                formAnswer: getLatestProtocol(protocolsHistory)?.protocolCreationFormAnswer,
              },
              protocolsHistory,
              formInputs,
            }),
          ),
          map((dialogOutputs) => {
            if (!dialogOutputs) {
              return null;
            }

            const { analysis, protocol } = dialogOutputs;
            const { pictures: analysisPictures, ...analysisFormAnswer } = analysis;

            // A sadm client without a protocol creation form gets the analysis form alone, so the
            // dialog completes without a protocol side.
            if (!protocol) {
              return {
                sadmEntityId,
                sadmEntityDraft,
                analysisFormAnswer,
                pictures: [...(analysisPictures ?? [])],
              };
            }

            const { pictures: protocolPictures, ...protocolFormAnswer } = protocol;
            return {
              sadmEntityId,
              sadmEntityDraft,
              analysisFormAnswer,
              protocolFormAnswer,
              pictures: [...(analysisPictures ?? []), ...(protocolPictures ?? [])],
            };
          }),
        ),
      ),
      catchError((err) => {
        console.warn('Error while creating analysis and protocol', err);
        return of(null);
      }),
      first(),
    );
  }

  editAnalysis({
    sadmEntityId,
    sadmClientId,
    formAnswer,
    formInputs,
  }: {
    sadmEntityId?: number;
    sadmClientId?: number;
    formAnswer: FormAnswer;
    formInputs: ModuleResolverInputs;
  }): Observable<{
    analysisFormAnswer: FormAnswer;
    pictures?: File[];
  }> {
    return this.getForms(sadmEntityId, sadmClientId).pipe(
      first(),
      switchMap(({ analysisForm }) => this.openDialogForm({ form: analysisForm, formAnswer, formInputs })),
      map((formOutputs) => {
        if (!formOutputs) {
          return null;
        }

        const { pictures, ...analysisFormAnswer } = formOutputs;
        return {
          analysisFormAnswer,
          pictures: [...(pictures ?? [])],
        };
      }),
    );
  }

  editAnalysisProtocol({
    sadmEntityId,
    sadmClientId,
    analysisFormAnswer,
    protocolFormAnswer,
    formInputs,
  }: {
    sadmEntityId?: number;
    sadmClientId?: number;
    analysisFormAnswer: FormAnswer;
    protocolFormAnswer: FormAnswer;
    formInputs: ModuleResolverInputs;
  }): Observable<{
    sadmEntityId?: number;
    sadmEntityDraft?: SadmEntityDraft;
    analysisFormAnswer: FormAnswer;
    protocolFormAnswer?: FormAnswer;
    pictures?: File[];
  }> {
    return this.getForms(sadmEntityId, sadmClientId).pipe(
      first(),
      switchMap(({ analysisForm, protocolForm, protocolsHistory }) =>
        this.openSadmDialogForm({
          analysis: analysisForm && { form: analysisForm, formAnswer: analysisFormAnswer },
          protocol: protocolForm && {
            form: protocolForm,
            formAnswer: protocolFormAnswer,
            // The protocol is persisted along with the analysis it belongs to, and the analysis
            // edition endpoint doesn't carry it: once the analysis is saved — so once its answer
            // holds an _id — the protocol can only be read.
            readOnly: !!analysisFormAnswer?._id,
          },
          protocolsHistory,
          formInputs,
        }),
      ),
      map((dialogOutputs) => {
        if (!dialogOutputs) {
          return null;
        }

        const { analysis, protocol } = dialogOutputs;
        const { pictures: analysisPictures, ...updatedAnalysisFormAnswer } = analysis;

        if (!protocol) {
          return {
            analysisFormAnswer: updatedAnalysisFormAnswer,
            pictures: [...(analysisPictures ?? [])],
          };
        }

        const { pictures: protocolPictures, ...updatedProtocolFormAnswer } = protocol;
        return {
          analysisFormAnswer: updatedAnalysisFormAnswer,
          protocolFormAnswer: updatedProtocolFormAnswer,
          pictures: [...(analysisPictures ?? []), ...(protocolPictures ?? [])],
        };
      }),
    );
  }

  private getOrCreateSadmEntity(
    clientCode: string,
    patientId: number,
    sadmEntityId?: number,
    formInputs?: ModuleResolverInputs,
  ): Observable<SadmEntityPickerOutput> {
    if (sadmEntityId) {
      return of({ sadmEntityId });
    }

    return this.dialogService.openComponent<SadmEntityPickerComponent, SadmEntityPickerOutput>(
      SadmEntityPickerComponent,
      {
        label: this.translocoService.translate('Documenter une lésion'),
        data: { clientCode, patientId, formInputs },
      },
    );
  }

  private getForms(
    sadmEntityId?: number,
    sadmClientId?: number,
  ): Observable<{
    creationForm: Form;
    analysisForm: Form;
    protocolForm?: Form;
    protocolsHistory: SadmProtocol[];
  }> {
    return sadmEntityId
      ? this.store.select(selectSadmEntity(sadmEntityId)).pipe(
          map(({ protocolsHistory, sadmClient }) => ({
            creationForm: sadmClient?.entityCreationForm,
            analysisForm: sadmClient?.analysisCreationForm,
            protocolForm: sadmClient?.protocolCreationForm,
            protocolsHistory: protocolsHistory ?? [],
          })),
        )
      : this.store.select(selectSadmClient(sadmClientId)).pipe(
          map((sadmClient) => ({
            creationForm: sadmClient?.entityCreationForm,
            analysisForm: sadmClient?.analysisCreationForm,
            protocolForm: sadmClient?.protocolCreationForm,
            protocolsHistory: [],
          })),
        );
  }

  private openDialogForm(data: FormComponentInputs): Observable<FormOutputs> {
    return this.dialogService.openComponent<DialogFormComponent, FormOutputs>(DialogFormComponent, {
      label: this.translocoService.translate('Documenter une lésion'),
      data,
      dismissible: false,
      size: 'm',
    });
  }

  private openSadmDialogForm(data: SadmDialogFormInput): Observable<SadmDialogFormOutput> {
    return this.dialogService.openComponent<SadmDialogFormComponent, SadmDialogFormOutput>(SadmDialogFormComponent, {
      label: this.translocoService.translate('Documenter une lésion'),
      data,
      dismissible: false,
      size: 'l',
    });
  }
}
